import * as S from './Footer.styles';
import { MEDIA } from '@/constants/styleConstants';
import Typography from '@/components/sushi/Typography';
import Button from '@/components/sushi/Button';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Key, useContext } from 'react';
import { IntlContext } from '@/contexts/intlContext';
import { footerLinks, socialLinks } from '@/components/Footer/links';
import Collapsible from '@/components/sushi/Collapsible/Collapsible';
import { useIntl } from 'react-intl';
import useWindowSize from '@/hooks/useWindowSize';

const Footer = ({ bannerTitle }: { bannerTitle?: string }) => {
  const router = useRouter();
  const locale = router.locale === 'fr' ? 'fr' : 'en';
  const { windowWidth } = useWindowSize();
  const { formatMessage } = useIntl();
  const { switchLang } = useContext(IntlContext);

  const setHref = (href: string, specifyLocale: boolean) => {
    if (specifyLocale) {
      return `${href}/${locale}`;
    } else if (!href.match('http|mailto') && router.pathname === '/') {
      return `${locale}/${href}`.replace(/\/\/+/, '/');
    } else {
      return href;
    }
  };

  const renderLinks = (section: string) => {
    return footerLinks[section].map(({ name, href, subsection, noTrad, specifyLocale }, index) => {
      if (subsection) return renderLinkSubsection(section, index);
      return (
        <li key={name}>
          {href.includes('http') ? (
            <a href={href} target="_blank">
              <Typography variant="bodyS" color="secondaryAlt">
                {noTrad ? name : formatMessage({ id: `footer.${name}` })}
              </Typography>
            </a>
          ) : (
            <Link href={setHref(href, specifyLocale)}>
              <Typography
                as="a"
                variant="bodyS"
                color="secondaryAlt"
                href={setHref(href, specifyLocale)}>
                {noTrad ? name : formatMessage({ id: `footer.${name}` })}
              </Typography>
            </Link>
          )}
        </li>
      );
    });
  };

  const renderLinkSubsection = (section: string, index: Key) => {
    return (
      <li key={index}>
        <Collapsible
          trigger={
            <Typography variant="bodyS" color="secondaryAlt">
              {footerLinks[section][index].name}
            </Typography>
          }>
          <S.SubsectionLinksWrapper>
            {footerLinks[section][index].subsection.map(({ name, href }) => {
              return (
                <li key={href}>
                  <Link href={href}>
                    <Typography as="a" variant="bodyS" color="secondaryAlt" href={href}>
                      {name}
                    </Typography>
                  </Link>
                </li>
              );
            })}
          </S.SubsectionLinksWrapper>
        </Collapsible>
      </li>
    );
  };

  const renderSocials = () => {
    return socialLinks.map(({ src, href }) => (
      <li key={src}>
        <Link href={href} passHref>
          <a target="_blank">
            <Image
              src={`/img/socials/${src}.svg`}
              alt="src"
              width="16"
              height="13"
              quality={100}
              loading="eager"
            />
          </a>
        </Link>
      </li>
    ));
  };

  return (
    <>
      {bannerTitle ? (
        <S.BannerWrapper>
          <S.Banner>
            <S.FirstCoin>
              <Image
                src="/img/coins/4.png"
                height="121"
                width="160"
                quality={100}
                loading="eager"
              />
            </S.FirstCoin>
            <Typography variant="headingMS" as="h4">
              {formatMessage({ id: bannerTitle })}
            </Typography>
            <span>
              <Link href="https://qxtk.adj.st/?adj_t=qhb6ybm&adj_campaign=websitefooter&adj_adgroup=CTA&adj_fallback=https%3A%2F%2Fapp.finary.com%2Fsignup&adj_redirect_macos=https%3A%2F%2Fapp.finary.com%2Fsignup">
                <a>
                  <Button variant="secondaryDark">
                    {formatMessage({ id: 'footer.banner.CTA' })}
                  </Button>
                </a>
              </Link>
            </span>
            <S.SecondCoin>
              <Image
                src="/img/coins/4.png"
                height="121"
                width="160"
                quality={100}
                loading="eager"
              />
            </S.SecondCoin>
          </S.Banner>
        </S.BannerWrapper>
      ) : null}
      <S.FooterWrapper withBanner={bannerTitle ? true : false}>
        <S.FooterContent>
          <S.LogoWrapper>
            <Image
              src="/img/logo-new.svg"
              alt="Finary Logo"
              width="175"
              height="51"
              quality={100}
              loading="eager"
            />
          </S.LogoWrapper>
          <S.LinksWrapper>
            <div>
              <Typography variant="bodyXS" css={S.linkListTitle} uppercase>
                {formatMessage({ id: 'footer.aboutFinary' })}
              </Typography>
              <S.LinkList>{renderLinks('about')}</S.LinkList>
            </div>
            {locale === 'fr' ? (
              <div>
                <Typography variant="bodyXS" css={S.linkListTitle} uppercase>
                  {formatMessage({ id: 'footer.invest' })}
                </Typography>
                <S.LinkList>{renderLinks('invest')}</S.LinkList>
              </div>
            ) : null}
            <div>
              <Typography variant="bodyXS" css={S.linkListTitle} uppercase>
                {formatMessage({ id: 'footer.legalInfo' })}
              </Typography>
              <S.LinkList>{renderLinks('legal')}</S.LinkList>
            </div>
          </S.LinksWrapper>
          <S.LastLineWrapper>
            {windowWidth >= MEDIA.SM ? (
              <S.FlagsWrapper>
                {router.locales.map((locale) => {
                  return (
                    <Link href={router.asPath} locale={locale} key={locale}>
                      <a>
                        <li onClick={() => switchLang(locale)}>
                          <Image
                            src={`/img/lang/${locale}.svg`}
                            alt={locale}
                            width={24}
                            height={18}
                            quality={100}
                            loading="eager"
                          />
                        </li>
                      </a>
                    </Link>
                  );
                })}
              </S.FlagsWrapper>
            ) : null}
            <S.SocialsWrapper>{renderSocials()}</S.SocialsWrapper>
          </S.LastLineWrapper>
          <Typography variant="bodyXXS" color="secondaryAlt">
            {formatMessage({ id: 'footer.copyright' })}
          </Typography>
        </S.FooterContent>
      </S.FooterWrapper>
    </>
  );
};

export default Footer;
